import type { AgentTaskType } from '@septima/contract';
// Agent metadata registry — the one source of truth for each agent's display
// code, human-readable role, owning surface, and accent colour. Shared by the
// Agents board (task cards), the Needs-Review item cards, the run-detail panel,
// and the activity feed so a given agent's colour and role label match across
// every surface.
//
// `role` is the plain-English job title shown next to the codename so a user who
// does not know the names still understands the agent (canonical wording:
// CONTEXT.md -> "Named agents"). It names the agent's CAPABILITY, not the single
// artefact it produces today, so it stays correct as an agent gains jobs. The
// Workflows editor keeps a parallel copy in
// `packages/workflow-ui/src/agent-roles.ts`; `agent-roles-parity.test.ts` fails
// the build if the two ever disagree.

export interface AgentMeta {
    code: string;
    /** Plain-English role/job title shown beside the codename. */
    role: string;
    /**
     * One-sentence plain-English description of what the agent does, shown in
     * the hover popover on every button that triggers it (so a user who does
     * not know the named agents still understands what they are about to run).
     * Wording tracks each agent's `summary` in
     * `packages/ontology-wealth/src/agents/<name>.ts`.
     */
    detail: string;
    taskTypes: AgentTaskType[];
    surface: 'desk' | 'ops' | 'comply' | 'view';
    /** Accent colour as an "r, g, b" triplet for `rgb(var(--agent))`. */
    color: string;
}

export const AGENTS: AgentMeta[] = [
    {
        code: 'ATLAS',
        role: 'Call & meeting analyst',
        detail: 'Transcribes and analyses client calls — summaries, fact-find prefills, monthly narratives and follow-ups.',
        taskTypes: [
            'post_call_processing',
            'call_classification_batch',
            'post_call_processing_batch',
            'fact_find_prefill_batch',
            'monthly_report',
            'monthly_narrative',
        ],
        surface: 'desk',
        color: '59, 130, 246',
    },
    {
        code: 'VIGIL',
        role: 'Suitability monitoring',
        detail: 'Monitors client portfolios for suitability breaches against their mandate and risk profile.',
        taskTypes: ['suitability_check'],
        surface: 'comply',
        color: '168, 85, 247',
    },
    {
        code: 'MASON',
        role: 'Reconciliation analysis',
        detail: 'Classifies open reconciliation breaks, proposing a likely cause and chronic flag for each with side-by-side evidence.',
        taskTypes: ['reconciliation_classification_batch'],
        surface: 'ops',
        color: '34, 197, 94',
    },
    {
        code: 'AEGIS',
        role: 'Compliance screening',
        detail: 'Screens for compliance issues — KYC freshness, breach drafts, Consumer Duty rescores and sanctions hits.',
        taskTypes: ['breach_draft', 'kyc_renewal', 'consumer_duty_rescore', 'sanctions_screening'],
        surface: 'comply',
        color: '239, 68, 68',
    },
    {
        code: 'CEDAR',
        role: 'Corporate-actions agent',
        detail: 'Routes pending corporate actions to the responsible IM via holdings; extracts corporate-action notices from uploaded PDFs into Reviewable drafts; explains a corporate action on demand with a plain-English breakdown for the IM.',
        taskTypes: ['coac_pdf_extraction', 'coac_breakdown'],
        surface: 'ops',
        color: '120, 113, 108',
    },
    {
        code: 'SCRIVENER',
        role: 'Drafting',
        detail: 'Drafts suitability and periodic-review letters in three tonal variants for review.',
        taskTypes: ['suitability_letter_draft'],
        surface: 'desk',
        color: '99, 102, 241',
    },
    {
        code: 'HERALD',
        role: 'Onboarding packs',
        detail: 'Assembles onboarding packs — welcome letter, terms of business, fees, disclosures and fact-find prompts.',
        taskTypes: ['onboarding_pack_draft'],
        surface: 'desk',
        color: '20, 184, 166',
    },
    {
        code: 'EMISSARY',
        role: 'Client correspondence',
        detail: 'Drafts outbound client communications in three tonal variants for review.',
        taskTypes: ['email_draft'],
        surface: 'desk',
        color: '244, 114, 182',
    },
    {
        code: 'WARDEN',
        role: 'Onboarding monitoring',
        detail: 'Scans onboarding cases for stalls, missing documents and approaching deadlines.',
        taskTypes: ['onboarding_pipeline_watch'],
        surface: 'ops',
        color: '251, 146, 60',
    },
    {
        code: 'SENTINEL',
        role: 'Vulnerability detection',
        detail: 'Scans clients for signs of vulnerability, routing markers to IM or Compliance for review.',
        taskTypes: ['vulnerability_flag'],
        surface: 'comply',
        color: '236, 72, 153',
    },
    {
        code: 'QUORUM',
        role: 'Portfolio-drift monitoring',
        detail: 'Scans portfolios for Construction-Framework deviations, distinguishing deliberate positions from drift.',
        taskTypes: ['framework_deviation'],
        surface: 'desk',
        color: '139, 92, 246',
    },
    {
        code: 'BEACON',
        role: 'Fund-policy monitoring',
        detail: 'Scans investment trusts for policy breaches and judges whether an RNS is triggered.',
        taskTypes: ['fund_policy_watch'],
        surface: 'view',
        color: '6, 182, 212',
    },
    {
        code: 'PULSE',
        role: 'Record-freshness monitoring',
        detail: 'Scans fact-find, suitability-review and CFL records for stale data needing a refresh.',
        taskTypes: ['data_freshness_check'],
        surface: 'comply',
        color: '132, 204, 22',
    },
    {
        code: 'CHARTER',
        role: 'Fund reporting',
        detail: 'Drafts investment-trust reporting — RNS, factsheets, board packs, shareholder letters, AIFMD and KIDs.',
        taskTypes: ['fund_rns_draft'],
        surface: 'view',
        color: '45, 212, 191',
    },
    {
        code: 'HAVEN',
        role: 'Safeguarding plans',
        detail: 'Drafts vulnerability adaptation plans — light-touch or full safeguarding.',
        taskTypes: ['vulnerability_adaptation_plan'],
        surface: 'comply',
        color: '248, 113, 113',
    },
    {
        code: 'FORUM',
        role: 'Governance papers',
        detail: 'Assembles governance papers — ManComm, IFC and board packs.',
        taskTypes: ['governance_paper_draft'],
        surface: 'view',
        color: '129, 140, 248',
    },
    {
        code: 'ARBITER',
        role: 'Fair-value assessments',
        detail: 'Produces Consumer Duty fair-value assessments for instruments under review.',
        taskTypes: ['fair_value_assessment'],
        surface: 'comply',
        color: '253, 186, 116',
    },
    {
        // WS-A — workflow runs surface on Ops as first-class AgentJobs.
        code: 'WORKFLOW',
        role: 'Workflow runner',
        detail: 'Runs a saved workflow graph end to end, producing one record per run.',
        taskTypes: ['workflow_run'],
        surface: 'ops',
        color: '234, 179, 8',
    },
];

/** Neutral slate accent for agents/items with no registered colour. */
export const NEUTRAL_AGENT_COLOR = '148, 163, 184';

/** Accent colour triplet for an agent code (ATLAS, VIGIL, …). */
export function agentColorForCode(code: string): string {
    return AGENTS.find((a) => a.code === code)?.color ?? NEUTRAL_AGENT_COLOR;
}

/**
 * Plain-English role for an agent code (e.g. PULSE -> "Record-freshness
 * monitoring"), or undefined if the code is unregistered. Shown beside the
 * codename across the app so users who do not know the names still understand
 * the agent.
 */
export function agentRoleForCode(code: string): string | undefined {
    return AGENTS.find((a) => a.code === code)?.role;
}

/** Codename + role + detail shown in an agent button's hover popover. */
export interface AgentHint {
    code: string;
    role: string;
    detail: string;
}

/**
 * Hint copy for a button that triggers a named agent. Accepts the canonical
 * code (`WARDEN`) or a job-suffixed detector code (`AEGIS-KYC`, `AEGIS-CD`) —
 * the suffix is stripped to resolve the owning agent, so all four AEGIS buttons
 * teach the same AEGIS card. Returns undefined for an unregistered code.
 */
export function agentHintForCode(code: string): AgentHint | undefined {
    const base = code.split('-')[0];
    const meta = AGENTS.find((a) => a.code === base);
    return meta ? { code: meta.code, role: meta.role, detail: meta.detail } : undefined;
}
